import { useCallback, useEffect, useMemo, useState } from "react";
import { NavigationArrow, MapPin, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StopTile, KIND_ORDER, todayIso, stopIsMine } from "@/pages/mobile/MobileStop";
import { stopKey } from "@/lib/routeStops";

// Applied dispatcher order wins; otherwise fall back to the natural kind order.
const byRoute = (a, b) => {
  const sa = a.route_seq,
    sb = b.route_seq;
  if (sa != null && sb != null) return sa - sb;
  if (sa != null) return -1;
  if (sb != null) return 1;
  return (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9);
};

// Deterministic pin position so the same route always draws the same shape.
const pinPos = (i, n) => {
  const t = n > 1 ? i / (n - 1) : 0.5;
  const y = 14 + t * 72;
  const x = 20 + ((i * 41) % 60);
  return { x, y };
};

export default function MobileMap() {
  const { user } = useAuth();
  const [allStops, setAllStops] = useState([]);
  const [mineOnly, setMineOnly] = useState(true);
  const date = todayIso();
  const [myLoc, setMyLoc] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [opt, setOpt] = useState(null);
  const [locBusy, setLocBusy] = useState(false);
  const [showDone, setShowDone] = useState(
    () => localStorage.getItem("mobile_show_done") === "1",
  );
  useEffect(() => {
    localStorage.setItem("mobile_show_done", showDone ? "1" : "0");
  }, [showDone]);
  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setMyLoc(`${p.coords.latitude},${p.coords.longitude}`);
        setLocBusy(false);
      },
      () => setLocBusy(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };
  const optimizeRoute = async () => {
    if (!stops.length) return;
    setOptimizing(true);
    try {
      const { data } = await api.post("/route/optimize", {
        origin: myLoc || user?.route_start || "",
        stops: stops.map((s) => ({
          id: stopKey(s),
          address: s.address || "",
          crew: s.crew || "Unassigned",
          label: s.project_name || "",
        })),
      });
      setOpt(data);
      if (!data.crews?.length) toast.error("Couldn't geocode these addresses");
    } catch (e) {
      toast.error("Route optimization failed");
    }
    setOptimizing(false);
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs/schedule", {
      params: { month: date.slice(0, 7) },
    });
    setAllStops(
      (data.events || [])
        .filter((e) => e.date === date && e.address)
        .sort(byRoute),
    );
  }, [date]);
  useEffect(() => {
    load();
  }, [load]);

  const baseStops = useMemo(
    () => (mineOnly ? allStops.filter((s) => stopIsMine(s, user)) : allStops),
    [allStops, mineOnly, user],
  );
  const stops = useMemo(
    () =>
      showDone
        ? baseStops
        : baseStops.filter((s) => (s.stop_status?.status || "") !== "Done"),
    [baseStops, showDone],
  );
  const hiddenDone = useMemo(
    () => baseStops.filter((s) => (s.stop_status?.status || "") === "Done").length,
    [baseStops],
  );

  const routeUrl = useMemo(() => {
    if (!stops.length) return "";
    const addrs = stops.map((s) => s.address);
    const origin = myLoc
      ? encodeURIComponent(myLoc)
      : user?.route_start
        ? encodeURIComponent(user.route_start)
        : "";
    const destination = encodeURIComponent(addrs[addrs.length - 1]);
    const waypoints = addrs.slice(0, -1).map(encodeURIComponent).join("|");
    let u = `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${destination}`;
    if (origin) u += `&origin=${origin}`;
    if (waypoints) u += `&waypoints=${waypoints}`;
    return u;
  }, [stops, user?.route_start, myLoc]);

  const region = useMemo(() => {
    const a = stops[0]?.address || "";
    const parts = a.split(",").map((p) => p.trim());
    return parts[1] || "Your route";
  }, [stops]);

  const pins = stops.map((s, i) => ({ s, i, ...pinPos(i, stops.length) }));

  return (
    <div className="px-4 pt-5" data-testid="mobile-map">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-title m-display text-3xl text-[#c39e6d]">Route</h1>
          <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            {stops.length} stop{stops.length === 1 ? "" : "s"} · today
          </p>
        </div>
        {stops.length > 0 && (
          <a
            href={routeUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-map-open-route"
            className="mt-1 inline-flex shrink-0 items-center gap-2 rounded-xl border border-zinc-700 bg-[#0F172A] px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-zinc-800"
          >
            Open in maps
            <ArrowRight size={14} weight="bold" />
          </a>
        )}
      </div>

      {stops.length > 0 && (
        <button
          type="button"
          data-testid="mobile-map-use-location"
          onClick={useMyLocation}
          className={`mt-3 flex w-full items-center justify-center gap-2 rounded-xl border py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
            myLoc
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-[#c39e6d]/40 bg-[#c39e6d]/10 text-[#c39e6d] hover:bg-[#c39e6d]/20"
          }`}
        >
          <NavigationArrow size={14} weight="bold" />
          {locBusy ? "Locating…" : myLoc ? "Routing from my location" : "Route from my location"}
        </button>
      )}

      {stops.length > 0 && (
        <button
          type="button"
          data-testid="mobile-map-optimize"
          onClick={optimizeRoute}
          disabled={optimizing}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#c39e6d] py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-950 transition-colors duration-150 hover:bg-[#c39e6d] disabled:opacity-50"
        >
          <NavigationArrow size={14} weight="bold" />
          {optimizing ? "Optimizing…" : "AI optimize route"}
        </button>
      )}
      {opt?.crews?.map((c) => (
        <div
          key={c.crew}
          data-testid={`mobile-route-crew-${c.crew}`}
          className="mt-3 overflow-hidden rounded-2xl border border-[#c39e6d]/30 bg-[#c39e6d]/5"
        >
          <div className="flex items-center justify-between border-b border-white/5 px-3 py-2.5">
            <span className="flex items-center gap-2 text-sm font-bold text-zinc-100">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              {c.crew}, {c.stops} stops
            </span>
            <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
              Optimized
            </span>
          </div>
          <div className="grid grid-cols-3 divide-x divide-white/5 text-center">
            <div className="py-2.5">
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Distance</div>
              <div className="text-base font-bold text-[#c39e6d]">{c.distance_mi} mi</div>
            </div>
            <div className="py-2.5">
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Drive</div>
              <div className="text-base font-bold text-zinc-100">
                {Math.floor(c.drive_min / 60)}h {c.drive_min % 60}m
              </div>
            </div>
            <div className="py-2.5">
              <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Saved</div>
              <div className="text-base font-bold text-emerald-400">{c.saved_pct}%</div>
            </div>
          </div>
          <ol className="space-y-1.5 px-3 pb-2 pt-2">
            {c.order.map((o, i) => (
              <li key={o.id} className="flex items-center gap-2 text-[13px] text-zinc-300">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#c39e6d] text-[10px] font-bold text-slate-950">
                  {i + 1}
                </span>
                <span className="truncate">{o.label || o.address}</span>
              </li>
            ))}
          </ol>
          {c.order.length > 0 && (
            <a
              href={(() => {
                const a = c.order.map((o) => encodeURIComponent(o.address));
                const origin = myLoc
                  ? encodeURIComponent(myLoc)
                  : user?.route_start
                    ? encodeURIComponent(user.route_start)
                    : "";
                let u = `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${a[a.length - 1]}`;
                if (origin) u += `&origin=${origin}`;
                const wp = a.slice(0, -1).join("|");
                if (wp) u += `&waypoints=${wp}`;
                return u;
              })()}
              target="_blank"
              rel="noreferrer"
              data-testid={`mobile-route-open-${c.crew}`}
              className="mx-3 mb-2 flex items-center justify-center gap-2 rounded-xl border border-[#c39e6d]/40 bg-[#c39e6d]/10 py-2 text-[11px] font-bold uppercase tracking-widest text-[#c39e6d] hover:bg-[#c39e6d]/20"
            >
              <NavigationArrow size={13} weight="bold" /> Open in Google Maps
            </a>
          )}
          <p className="px-3 pb-2 text-[9px] uppercase tracking-widest text-zinc-600">
            {opt.provider === "estimate" ? "Estimated · add Google key for exact times" : "Google Directions"}
          </p>
        </div>
      ))}

      <div className="mt-4 flex gap-2" data-testid="mobile-map-filter">
        {[
          [true, "My route"],
          [false, "Whole shop"],
        ].map(([v, l]) => (
          <button
            key={l}
            data-testid={`mobile-map-filter-${v ? "mine" : "shop"}`}
            onClick={() => setMineOnly(v)}
            className={`h-9 flex-1 rounded-xl border text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              mineOnly === v
                ? "border-[#c39e6d] bg-[#c39e6d] text-slate-950"
                : "border-zinc-800 bg-[#0F172A] text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {hiddenDone > 0 && (
        <button
          type="button"
          data-testid="mobile-map-toggle-done"
          onClick={() => setShowDone((v) => !v)}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1 text-[11px] font-semibold text-zinc-400 hover:bg-zinc-800"
        >
          {showDone ? "Hide" : "Show"} {hiddenDone} completed
        </button>
      )}

      {stops.length === 0 ? (
        <div
          data-testid="mobile-map-empty"
          className="mt-6 rounded-2xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500"
        >
          {mineOnly ? "No stops on your route today." : "No stops with an address today."}
        </div>
      ) : (
        <>
          <div
            className="relative mt-4 overflow-hidden rounded-3xl border border-[#c39e6d]/25 bg-gradient-to-b from-[#c39e6d]/10 via-zinc-950 to-zinc-950"
            data-testid="mobile-map-canvas"
            style={{ aspectRatio: "1 / 1.05" }}
          >
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(99,102,241,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.18) 1px, transparent 1px)",
                backgroundSize: "34px 34px",
              }}
            />
            <span className="absolute right-3 top-3 rounded-lg bg-[#080C14]/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 ring-1 ring-white/5">
              {region}
            </span>

            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polyline
                points={pins.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="none"
                stroke="#818cf8"
                strokeWidth="0.7"
                strokeDasharray="2.2 2"
                strokeLinecap="round"
                opacity="0.8"
              />
            </svg>

            {pins.map(({ s, i, x, y }) => {
              const status = s.stop_status?.status || "Pending";
              const done = status === "Done";
              const onSite = status === "On site";
              const bg = done ? "bg-emerald-500" : onSite ? "bg-yellow-400" : "bg-[#c39e6d]";
              return (
                <a
                  key={`${s.job_id}-${s.kind}-${s.stop_id || ""}`}
                  href={`https://maps.google.com/?q=${encodeURIComponent(s.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`mobile-map-pin-${s.job_id}-${s.kind}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${x}%`, top: `${y}%` }}
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold text-zinc-950 shadow-[0_0_18px_-2px_rgba(99,102,241,0.9)] ring-2 ring-zinc-950 ${bg}`}
                  >
                    {done ? "✓" : i + 1}
                  </span>
                </a>
              );
            })}
          </div>

          <div className="mb-3 mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-zinc-800" />
            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500">
              Stops in order
            </span>
            <span className="h-px flex-1 bg-zinc-800" />
          </div>
          <div className="space-y-2.5" data-testid="mobile-map-list">
            {stops.map((s, i) => (
              <StopTile
                key={`${s.job_id}-${s.kind}-${s.stop_id || ""}`}
                s={s}
                index={i + 1}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
